import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Link as MuiLink,
  List,
  ListItem,
  ListItemButton,
  OutlinedInput,
  Skeleton,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { neutral, palette } from '@ahora-patagonia/design-tokens';
import { TiraLateral } from '../../../shared/lateral/TiraLateral.js';
import { SECCION_KIND } from '../../../seccion/models/entities/SeccionData.js';
import { ListadoNotas } from '../../../shared/notas/ListadoNotas.js';
import { IconoBuscar } from '../../../shared/components/iconos.js';
import { useObservable } from '../../../shared/hooks/useObservable.js';
import { useViewModel } from '../../../shared/hooks/useViewModel.js';
import { useSincronizarPagina } from '../../../shared/shell/paginaPublica.js';
import { useSenalDePagina } from '../../../shared/senales/useSenalDePagina.js';
import { useHead } from '../../../shared/seo/useHead.js';
import { useSitio, useSsrData } from '../../../shared/ssr/SsrDataProvider.js';
import { headDeBusqueda } from '../../buscar.head.js';
import { BUSCAR_ROUTE_KEY } from '../../buscar.loader.js';
import type { BusquedaData } from '../../models/entities/BusquedaData.js';
import { BusquedaViewModel, limpiarTermino } from '../../viewmodels/BusquedaViewModel.js';

/**
 * Cuánto se espera después de la última tecla antes de pedir.
 *
 * Corto porque el endpoint está cacheado y responde en milisegundos; lo único que evita es
 * mandar un pedido por cada letra de una palabra tipeada de corrido.
 */
const REBOTE_MS = 250;

/**
 * La búsqueda: mismo esqueleto que la sección (columna principal + tira lateral).
 *
 * Es **incremental**: entra mostrando todas las noticias y cada letra que se escribe (o borra)
 * recorta la lista. El campo no tiene botón —el título ya dice Buscar y la lupa va a la
 * derecha— porque no hay nada que apretar. Enter igual funciona, y se saltea el rebote.
 *
 * El caso sin resultados ofrece **las categorías como salida** (nombre + cantidad, filas de
 * 48 px), no chips sueltos.
 */
export function BuscarView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const sitio = useSitio();
  const initial = useSsrData<BusquedaData>(BUSCAR_ROUTE_KEY);
  const vm = useViewModel(() => new BusquedaViewModel(initial, q));
  const state = useObservable(vm.state$);

  /**
   * Lo que hay tipeado, separado de `q` (la URL). Hacen falta los dos: el campo tiene que
   * responder a cada tecla, y la URL se actualiza con rebote.
   */
  const [texto, setTexto] = useState(q);

  // El término efectivo viaja en la URL: así la búsqueda se puede compartir y el SSR la
  // resuelve en el server. `replace` para no dejar una entrada de historial por letra.
  useEffect(() => {
    if (limpiarTermino(texto) === q) return;
    const rebote = setTimeout(() => {
      const limpio = limpiarTermino(texto);
      setSearchParams(limpio ? { q: limpio } : {}, { replace: true });
    }, REBOTE_MS);
    return () => clearTimeout(rebote);
  }, [texto, q, setSearchParams]);

  // Navegación externa (atrás/adelante, o un link con `?q=`): el campo sigue a la URL. Depende
  // solo de `q` justamente para no pisar lo que se está tipeando.
  useEffect(() => {
    setTexto((actual) => (limpiarTermino(actual) === q ? actual : q));
  }, [q]);

  useEffect(() => {
    void vm.buscar(q);
  }, [vm, q]);
  useSincronizarPagina(state.data);
  // Una vez por llegada al buscador y no por término: filtra mientras se tipea, y cada pausa del
  // teclado sería una "página" más.
  useSenalDePagina('BUSQUEDA', state.data ? 'buscar' : null);
  // El término va en la URL, así que el `<head>` lo sigue: mismo armador que el loader.
  useHead(useMemo(() => headDeBusqueda(q, sitio), [q, sitio]));

  const data = state.data;
  const items = state.items;
  // Solo cuando no hay nada que mostrar todavía: durante el tipeo la lista anterior se queda
  // a la vista (apenas atenuada) en lugar de colapsar y volver a crecer con cada letra.
  const mostrarEsqueleto = state.loading && items.length === 0;

  return (
    <>
      <Box sx={{ maxWidth: 880 }}>
        <Typography variant="tituloNota" component="h1">
          Buscar
        </Typography>
        <Box
          component="form"
          role="search"
          sx={{ mt: 2 }}
          onSubmit={(e) => {
            e.preventDefault();
            const limpio = limpiarTermino(texto);
            setSearchParams(limpio ? { q: limpio } : {}, { replace: true });
          }}
        >
          <OutlinedInput
            name="q"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            fullWidth
            autoComplete="off"
            placeholder="Filtrar por título"
            inputProps={{ 'aria-label': 'Buscar por título' }}
            endAdornment={
              <InputAdornment position="end">
                <IconoBuscar />
              </InputAdornment>
            }
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' },
          gap: { xs: 0, lg: 4 },
          alignItems: 'stretch',
          mt: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {state.error && <Alert severity="error">{state.error}</Alert>}
          {mostrarEsqueleto && (
            <>
              <Skeleton variant="rectangular" height={96} sx={{ mt: 1 }} />
              <Skeleton variant="rectangular" height={96} sx={{ mt: 1 }} />
            </>
          )}

          {!mostrarEsqueleto && data && (
            <Typography
              variant="firma"
              component="p"
              aria-live="polite"
              sx={{ color: neutral[700], mb: 1 }}
            >
              {resumen(data.q, data.total)}
            </Typography>
          )}

          {!mostrarEsqueleto && items.length > 0 && (
            // Atenuada mientras viaja el pedido del término nuevo: se ve que algo está
            // pasando sin que la lista se vaya de la pantalla.
            <Box sx={{ opacity: state.loading ? 0.5 : 1, transition: 'opacity 120ms' }}>
              <ListadoNotas notas={items} />
              {data && items.length < data.total && (
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={state.cargandoMas}
                  onClick={() => void vm.verMas()}
                  sx={{ mt: 2 }}
                >
                  {state.cargandoMas ? 'Cargando…' : 'Ver más'}
                </Button>
              )}
            </Box>
          )}

          {!mostrarEsqueleto && data && data.q && items.length === 0 && (
            <Box component="section" sx={{ mt: 2 }}>
              <Typography
                variant="tituloBloque"
                component="h2"
                sx={{ borderBottom: `2px solid ${palette.ink}`, pb: 0.5 }}
              >
                Recorré por sección
              </Typography>
              <List sx={{ py: 0 }}>
                {data.shell.nav.map((s) => (
                  <ListItem
                    key={s.key}
                    disablePadding
                    sx={{ borderBottom: `1px solid ${neutral[300]}` }}
                  >
                    <ListItemButton
                      component={RouterLink}
                      to={s.path}
                      sx={{ minHeight: 48, justifyContent: 'space-between', px: 1 }}
                    >
                      <Typography component="span" sx={{ fontWeight: 800, fontSize: 15 }}>
                        {s.name}
                      </Typography>
                      {s.articleCount !== null && (
                        <Typography component="span" sx={{ color: neutral[600], fontSize: 13 }}>
                          {s.articleCount} {s.articleCount === 1 ? 'nota' : 'notas'}
                        </Typography>
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <MuiLink component={RouterLink} to="/" sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 48 }}>
                Ir a la portada
              </MuiLink>
            </Box>
          )}
        </Box>

        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <TiraLateral
            notas={data?.ultimaHora ?? []}
            titulo={data?.shell.ultimaHoraNombre}
            verTodo={`/${SECCION_KIND.ultimaHora}`}
            ad={data?.ads.SIDEBAR}
          />
        </Box>
      </Box>
    </>
  );
}

/** Qué dice la línea de arriba de la lista según haya término o no. */
function resumen(q: string, total: number): string {
  if (!q) {
    return total === 0
      ? 'Todavía no hay noticias publicadas'
      : `Todas las noticias · ${total}`;
  }
  if (total === 0) return `Sin resultados para “${q}”`;
  return `${total} ${total === 1 ? 'resultado' : 'resultados'} para “${q}”`;
}
